/**
 * JS component for handling the behavior of the modal feature
 */

const VX_FOREGORUND_CONTAINER = '.vx_foreground-container';
const DATA_SCROLL_POSITON = 'data-scroll-position';
const ARIA_HIDDEN = 'aria-hidden';

function addInertAttToGlobalNav({ globalNavContainer, foregroundContainer }) {
  if (globalNavContainer && globalNavContainer.setAttribute) {
    globalNavContainer.setAttribute('inert', true);
    globalNavContainer.setAttribute(ARIA_HIDDEN, true);
  }
  if (foregroundContainer && foregroundContainer.setAttribute) {
    foregroundContainer.setAttribute('inert', true);
    foregroundContainer.setAttribute(ARIA_HIDDEN, true);
  }
}

function removeInertAttToGlobalNav({ globalNavContainer, foregroundContainer }) {
  if (globalNavContainer && globalNavContainer.removeAttribute) {
    globalNavContainer.removeAttribute('inert', true);
    globalNavContainer.removeAttribute(ARIA_HIDDEN, true);
  }
  if (foregroundContainer && foregroundContainer.removeAttribute) {
    foregroundContainer.removeAttribute('inert', true);
    foregroundContainer.removeAttribute(ARIA_HIDDEN, true);
  }
}

function getElForFocus({ openingTriggerEl, elSelectorToReceiveFocus, doc, noopObj }) {
  return (
    openingTriggerEl || // Assign trigger
    (elSelectorToReceiveFocus && doc.querySelector(elSelectorToReceiveFocus)) || // Or, assign selector
    noopObj
  ); // Finally, assign noop if none exist
}

function getModalEl({ modalId, doc }) {
  return modalId ? doc.getElementById(modalId) : doc.querySelector('.vx_modalIsOpen');
}

export function setAriaLabelModalDialog(modalEl) {
  if (modalEl && !modalEl.hasAttribute('aria-label')) {
    modalEl.setAttribute('aria-label', 'Modal Dialog');
  }
}

export function addEventListenerOrTimeout({ doc, modalEl, finishTransition }) {
  if (doc.body.style.transition === '') {
    modalEl?.addEventListener('transitionend', finishTransition);
  } else {
    setTimeout(finishTransition, 220);
  }
}

export function addEventListenerKeyup({ event, root, doc, openModal, closeVxModal }) {
  if (
    event.keyCode === 27 && // Is key Esc key?
    root.className.indexOf('vx_hasOpenModal') !== -1
  ) {
    // Is modal open?

    // Grab the open modal
    openModal = doc.querySelector('.vx_modalIsOpen');
    // If the X button has `-trigger` appended to it, we control it
    if (openModal?.querySelector('.vx_modal-dismiss_x-trigger')) {
      // Since we control it, let's close it!
      closeVxModal();
    }
  }
}

function getRoot(doc) {
  return doc.getElementById('vx_root') || doc.body;
}

function getScrollPos(doc) {
  return doc.documentElement.scrollTop || doc.body.parentNode.scrollTop || doc.body.scrollTop;
}

export default function initModals(win, doc) {
  'use strict';

  /**
   * Grab the main parent element for event delegation, rather than
   * attaching an event listener to each individual floating label instance.
   */
  const root = getRoot(doc);
  const noopObj = {
    // Used for knowing what to return focus to upon closing
    focus: function noop() {}, // noop to prevent thing is not a function crash
  };
  let openingTriggerEl = null; // For caching the trigger element that opens modal

  function openVxModal(modalId) {
    /**
     * Grab scroll position for preserving during modal operations.
     * The multiple attempts are because of nasty browser inconsistencies :(
     * Then, grab reference to the desired modal element.
     */
    const scrollPos = getScrollPos(doc);
    const modalEl = doc.getElementById(modalId);
    const globalNavContainer = doc.querySelector('.vx_globalNav-main');
    const foregroundContainer = doc.querySelector(VX_FOREGORUND_CONTAINER);
    /**
     * @function finishTransition - responsible for cleaning up after transitions
     */
    function finishTransition() {
      if (!doc.querySelector(VX_FOREGORUND_CONTAINER)) {
        return;
      }
      /**
       * Preserve the original scroll position by positioning the element with `top` prop
       */
      doc.querySelector(VX_FOREGORUND_CONTAINER).setAttribute('style', `top: -${scrollPos}px;`);
      /**
       * Save the scroll position to the body for later retrieval
       */
      root.setAttribute(DATA_SCROLL_POSITON, scrollPos);
      /**
       * Set the scroll position to 0 to ensure the top of the modal is visible,
       * if the view was scrolled down to click on the trigger
       */
      doc.body.scrollTop = 0;
      /**
       * Add and remove the appropriate classes
       */
      root.classList.add('vx_hasOpenModal');
      modalEl.classList.remove('vx_modalIsOpening');
      modalEl.classList.add('vx_modalIsOpen');
      /**
       * Remove event listener.
       */
      modalEl.removeEventListener('transitionend', finishTransition);
      /**
       * Focus on modal container
       */
      modalEl.focus();
      /**
       * This adds the inert attribute to the global nav and the foreground container
       * This is for removing the non-modal elements from assistive technologies,
       * aka "focus trapping"
       *
       * This currently depends on the polyfil seen here: https://github.com/WICG/inert.
       * This is packaged up with this library if you use the whole built js file in dist.
       */
      addInertAttToGlobalNav({ globalNavContainer, foregroundContainer });
    }
    /**
     * Prep the modal for animation. I.e. add change from `display: none`
     */
    modalEl.classList.add('vx_modalPrepToOpen');
    /**
     * What for DOM to update and register new `display` prop for both modal and BG
     */
    setTimeout(function () {
      modalEl.classList.add('vx_modalIsOpening');
      /**
       * Add minimal a11y support in case no aria describedby is used
       */
      setAriaLabelModalDialog(modalEl);
    }, 35);
    /**
     * Does client have transitionEnd event?
     * If not, fallback to setTimeout
     */
    // eslint-disable-next-line no-undef
    addEventListenerOrTimeout({
      doc,
      modalEl,
      finishTransition,
    });
  }
  /**
   * @function closeVxModal – the function that handles closing the modal,
   * used for both event and programmatic control
   * @param {string} modalId
   * @param {string} elSelectorToReceiveFocus – optional, pass in selector for element that receives
   * focus once the modal is closed. A11y!
   */
  function closeVxModal(modalId = null, elSelectorToReceiveFocus = null) {
    /**
     * Grab original scroll position before opening the modal.
     * Grab modal reference or just grab the current open modal element.
     */
    const scrollPos = root.getAttribute(DATA_SCROLL_POSITON);
    const modalEl = getModalEl({
      modalId,
      doc,
    });
    const globalNavContainer = doc.querySelector('.vx_globalNav-main');
    const foregroundContainer = doc.querySelector(VX_FOREGORUND_CONTAINER);
    /**
     * @function finishTransition - responsible for cleaning up after transitions
     */
    function finishTransition() {
      const elForFocus = getElForFocus({
        openingTriggerEl,
        elSelectorToReceiveFocus,
        doc,
        noopObj,
      });
      /**
       * Remove the "faked" scroll position
       */
      doc.querySelector(VX_FOREGORUND_CONTAINER).removeAttribute('style');
      /**
       * Grab the original scroll position and add it back to the body
       */
      root.removeAttribute(DATA_SCROLL_POSITON);
      doc.body.scrollTop = scrollPos;
      /**
       * Add and remove the appropriate classes
       */
      modalEl.classList.remove('vx_modalIsClosing');
      modalEl.classList.remove('vx_modalPrepToOpen');
      /**
       * Remove event listener.
       */
      modalEl.removeEventListener('transitionend', finishTransition);
      elForFocus.focus(); // place focus back on original triggering element
      openingTriggerEl = null; // Empty trigger element
    }
    /**
     * This adds the inert attribute to the global nav and the foreground container
     * This is for removing the non-modal elements from assistive technologies,
     * aka "focus trapping"
     *
     * This currently depends on the polyfill seen here: https://github.com/WICG/inert.
     * This is packaged up with this library if you use the whole built js file in dist.
     */
    removeInertAttToGlobalNav({ globalNavContainer, foregroundContainer });
    /**
     * Prep body and modal for close animation
     */
    root.classList.remove('vx_hasOpenModal');
    modalEl?.classList.remove('vx_modalIsOpen');
    modalEl?.classList.add('vx_modalIsClosing');
    /**
     * Does client have transitionEnd event?
     */
    // eslint-disable-next-line no-undef
    addEventListenerOrTimeout({
      doc,
      modalEl,
      finishTransition,
    });
  }
  /**
   * @function - adds `keyup` event listener to container element
   *
   * Note: listens for ESC key only
   */
  root.addEventListener('keyup', function (event) {
    const openModal = null;

    addEventListenerKeyup({
      event,
      root,
      doc,
      openModal,
      closeVxModal,
    });
  });

  if (!win.CWHelpers.modal) {
    win.CWHelpers.modal = {};
  }
  /**
   * Expose open and close for programmatic control
   */
  win.CWHelpers.modal.open = openVxModal;
  win.CWHelpers.modal.close = closeVxModal;
}
